import { Type } from 'class-transformer'
import { Entity } from '..'

import { Person, Phone, Photo } from '../persons'
import { LeadActivity, Lead } from '../crm'
import {
  Appraisal,
  AppraisalResponse,
  Enablement,
  Inspection,
  InspectionQualification,
  NegotiationResponse,
} from '../purchase'
import { EmployeePosition } from './EmployeePosition'
import { ProcessStatus } from '../settings'

export class EmployeeSchedule {
  start: string
  end: string
}

export class EmployeeMetadata {
  displayOnWebsite: boolean
  schedule: {
    weekdays: EmployeeSchedule
    saturday: EmployeeSchedule
  }
}

export class LeadRule {
  enabledForReception: boolean
  enabledPipelines: {
    sale: boolean
    renewal: boolean
    purchase: boolean
    indefinite: boolean
    // eslint-disable-next-line camelcase
    financed_sale: boolean
    // eslint-disable-next-line camelcase
    financed_renovation: boolean
    consignment: boolean
  }
}

export class Employee extends Entity {
  @Type(() => Person)
  person: Person;

  @Type(() => Lead)
  executiveLeads: Lead[];

  @Type(() => Lead)
  forwardedLeads: Lead[];

  @Type(() => LeadActivity)
  leadActivities: LeadActivity[];

  @Type(() => Appraisal)
  appraisals: Appraisal[];

  @Type(() => AppraisalResponse)
  appraisalResponses: AppraisalResponse[];

  @Type(() => Enablement)
  enablements: Enablement[];

  @Type(() => LeadRule)
  leadRule: LeadRule;

  @Type(() => Inspection)
  inspectorInspections: Inspection[];

  @Type(() => Inspection)
  supervisorInspections: Inspection[];

  @Type(() => InspectionQualification)
  inspectionQualifications: InspectionQualification[];

  @Type(() => NegotiationResponse)
  negotiationResponses: NegotiationResponse[];

  @Type(() => EmployeeMetadata)
  metadata: EmployeeMetadata;

  @Type(() => EmployeePosition)
  positions: EmployeePosition[];

  @Type(() => ProcessStatus)
  status: ProcessStatus

  _selected: boolean

  get name (): string {
    const { person } = this
    return person?.name
  }

  get photo (): Photo {
    const { person } = this
    return person?.photo
  }

  get phone (): string {
    const { person } = this

    return person?.secondaryPhone?.work
  }

  get email (): string {
    const { person } = this
    return person?.email?.toString()
  }

  get whatsappMe (): string {
    const { person } = this
    return person?.whatsappMeWork
  }

  get available (): boolean {
    // TODO: determinate current employee availability.
    return true
  }

  get selected (): boolean {
    const { _selected } = this
    // TODO: Compute the selected value
    return _selected
  }

  get secondaryPhone (): Phone {
    const { person } = this

    return person?.secondaryPhone
  }

  get admissionDate () {
    const { createdAt } = this
    return createdAt.format('DD/MM/YYYY')
  }

  get positionName () {
    const { positions } = this

    if (!positions?.length) return

    return positions?.[0]?.positionType?.fullName
  }

  // ALERTAS
  get alerts () {
    const { leadReceptionAlert, inactivePositionAlert, displayOnWebsiteAlert } = this

    return [leadReceptionAlert, inactivePositionAlert, displayOnWebsiteAlert].filter(Boolean)
  }

  get displayOnWebsiteAlert () {
    const { metadata } = this

    if (!metadata?.displayOnWebsite) return

    return {
      background: undefined,
      color: '#13CE66',
      icon: 'mdi-web',
      tooltip: 'Activo en página web',
    }
  }

  get inactivePositionAlert () {
    const { positions } = this

    if (!positions?.length) return

    if (positions.some(position => !position?.positionType?.active)) {
      return {
        background: undefined,
        color: '#101038',
        icon: 'mdi-account-off',
        tooltip: 'Cargo inactivo',
      }
    }
  }

  get leadReceptionAlert () {
    const { leadRule } = this

    if (!leadRule?.enabledForReception) return

    const pipelines = {
      purchase: 'compra',
      consignment: 'consignación',
      sale: 'venta',
      renewal: 'renovación',
      financed_sale: 'venta con crédito',
      financed_renovation: 'renovación con crédito',
      indefinite: 'por definir',
    }

    const tooltipPipelines = Object.entries(pipelines)
      .filter(pipeline => leadRule?.enabledPipelines[pipeline[0]])
      .map(pipeline => pipeline[1])
      .join(', ')

    return {
      background: undefined,
      icon: '$funnel',
      color: '#6185DB',
      tooltip: `Habilitado para recibir leads (${tooltipPipelines})`,
      disableElevation: true,
    }
  }
}
